/**
 * Inertia.js Application Entry Point
 *
 * KEY ARCHITECTURAL DECISIONS:
 *
 * 1. NO InertiaThemeSync component wrapper (CRITICAL)
 *    - usePage() subscribes to ALL prop changes, causing full re-renders on every request
 *    - This was THE ROOT CAUSE of input focus loss during typing
 *    - Solution: Theme/preferences initialized once in setup() instead
 *
 * 2. Using 'only' parameter in filter requests (REQUIRED)
 *    - Pages that update via filters (search, pagination) MUST use:
 *      router.get(url, params, { only: ['data', 'pagination', 'filters'] })
 *    - This prevents shared props (auth, preferences, flash) from updating
 *    - Maintains input focus during typing by avoiding unnecessary re-renders
 *
 * 3. NO React.StrictMode wrapper (OPTIONAL)
 *    - Removed for consistency with temp-starter-base (working reference)
 *    - Note: StrictMode only double-renders in development, not production
 *    - Can be added back if needed - InertiaThemeSync was the real culprit
 *
 * 4. Eager loading with import.meta.glob (OPTIONAL)
 *    - Loads all pages at build time for better initial performance
 *    - Alternative: Remove { eager: true } for code splitting and lazy loading
 */

import React from 'react'
import { createRoot } from 'react-dom/client'
import { createInertiaApp, router } from '@inertiajs/react'
import { AppLayout } from '@/layouts/app-layout'
import { ErrorBoundary } from '@/components/error-boundary'
import { pageStore } from '@/lib/page-store'
import authService from '@/lib/auth'

// Import CSS
import './application.css'

// Safari 18.x workaround: Replace ReactSharedInternals.H plain property with
// a getter/setter pair. Safari's JavaScriptCore JIT may cache the initial null
// value of this property and return stale reads even after react-dom sets it to
// a real hooks dispatcher in renderWithHooks(). Using Object.defineProperty
// forces the engine through a getter call on every access, preventing the stale
// cache from being used. This is a transparent interceptor — behaviour is
// identical to a plain property, but avoids the JIT optimisation bug.
const isSafari = typeof navigator !== 'undefined' &&
  /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)

if (isSafari) {
  const internals = (React as any).__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE
  if (internals) {
    let _h = internals.H
    Object.defineProperty(internals, 'H', {
      get() { return _h },
      set(val: any) { _h = val },
      configurable: true,
      enumerable: true,
    })
  }
}

// Global error handler for module-level errors (missing npm packages, syntax errors)
// These errors happen BEFORE React mounts, so ErrorBoundary can't catch them
function showGlobalError(error: Error | string, source?: string) {
  const appElement = document.getElementById('app')
  if (!appElement) return

  const errorMessage = typeof error === 'string' ? error : error.message
  const errorStack = typeof error === 'object' && error.stack ? error.stack : ''
  const isDev = import.meta.env?.DEV ?? true

  // Format error for Claude
  const formatErrorForClaude = () => {
    let message = `## Module/Import Error\n\n`
    message += `**Error:** ${errorMessage}\n\n`
    if (source) {
      message += `**Source:** ${source}\n\n`
    }
    if (errorStack) {
      const shortStack = errorStack.split('\n').slice(0, 6).join('\n')
      message += `**Stack Trace:**\n\`\`\`\n${shortStack}\n\`\`\``
    }
    return message
  }

  // Store error text for copy function
  ;(window as any).__cayuErrorText = formatErrorForClaude()

  appElement.innerHTML = `
    <div style="min-height: 100vh; background: #f9fafb; display: flex; align-items: center; justify-content: center; padding: 1rem; font-family: system-ui, -apple-system, sans-serif;">
      <div style="max-width: 56rem; width: 100%;">
        <div style="background: #fef2f2; border: 1px solid #fecaca; ${isDev && errorStack ? 'border-radius: 0.5rem 0.5rem 0 0;' : 'border-radius: 0.5rem;'} padding: 1.5rem;">
          <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;">
            <div style="display: flex; align-items: flex-start; gap: 1rem; min-width: 0;">
              <svg style="width: 2rem; height: 2rem; color: #ef4444; flex-shrink: 0;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div style="min-width: 0;">
                <h1 style="font-size: 1.25rem; font-weight: 600; color: #991b1b; margin: 0;">Module Error</h1>
                <p style="margin: 0.25rem 0 0 0; font-size: 0.875rem; color: #b91c1c; word-break: break-word;">${escapeHtml(errorMessage)}</p>
              </div>
            </div>
            <div style="display: flex; flex-shrink: 0; gap: 0.5rem;">
              <button id="send-to-chat-btn" style="display: inline-flex; align-items: center; padding: 0.375rem 0.75rem; font-size: 0.875rem; font-weight: 500; color: white; background: #dc2626; border: none; border-radius: 0.375rem; cursor: pointer;">
                <svg style="width: 1rem; height: 1rem; margin-right: 0.375rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Send to Chat
              </button>
              <button onclick="window.location.reload()" style="display: inline-flex; align-items: center; padding: 0.375rem 0.75rem; font-size: 0.875rem; font-weight: 500; color: white; background: #2563eb; border: none; border-radius: 0.375rem; cursor: pointer;">
                Reload
              </button>
            </div>
          </div>
        </div>
        ${isDev && errorStack ? `
        <div style="background: white; border: 1px solid #fecaca; border-top: none; border-radius: 0 0 0.5rem 0.5rem; padding: 1.5rem;">
          <h2 style="font-size: 0.875rem; font-weight: 500; color: #6b7280; margin: 0 0 0.5rem 0;">Stack Trace</h2>
          <pre style="background: #f3f4f6; border-radius: 0.375rem; padding: 1rem; overflow-x: auto; font-size: 0.75rem; font-family: monospace; color: #1f2937; white-space: pre-wrap; word-break: break-word; max-height: 16rem; overflow-y: auto; margin: 0;">${escapeHtml(errorStack)}</pre>
          ${source ? `<p style="margin: 1rem 0 0 0; font-size: 0.75rem; color: #6b7280;">Source: ${escapeHtml(source)}</p>` : ''}
        </div>
        ` : ''}
      </div>
    </div>
  `

  // Add click handler for "Send to Chat" button
  const sendBtn = document.getElementById('send-to-chat-btn')
  if (sendBtn) {
    sendBtn.addEventListener('click', () => {
      const errorText = (window as any).__cayuErrorText
      // Dispatch event for cayu-sdk to handle (sends to parent Cayu platform)
      window.dispatchEvent(new CustomEvent('cayu:send-error-to-chat', { detail: { message: errorText } }))

      // Visual feedback
      sendBtn.innerHTML = `
        <svg style="width: 1rem; height: 1rem; margin-right: 0.375rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        Sent!
      `
      sendBtn.style.background = '#16a34a'
      setTimeout(() => {
        sendBtn.innerHTML = `
          <svg style="width: 1rem; height: 1rem; margin-right: 0.375rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Send to Chat
        `
        sendBtn.style.background = '#dc2626'
      }, 2000)
    })
  }

}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

// Track whether React has mounted - only show full-page errors before mount
// After React mounts, ErrorDialog handles errors instead
let reactMounted = false

// Catch unhandled errors (module loading, async errors, etc.)
window.addEventListener('error', (event) => {
  // Only handle if it's a script error (not resource loading error)
  // AND only before React mounts (after mount, ErrorDialog handles it)
  if (event.error && !reactMounted) {
    console.error('Global error caught:', event.error)
    showGlobalError(event.error, event.filename)
  }
})

// Catch unhandled promise rejections (only before React mounts)
window.addEventListener('unhandledrejection', (event) => {
  if (!reactMounted) {
    console.error('Unhandled promise rejection:', event.reason)
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    showGlobalError(error, 'Promise rejection')
  }
})

const appElement = document.getElementById('app')

if (appElement) {
  // Track if we're currently refreshing to prevent infinite loops
  let isRefreshing = false

  // Configure Inertia to include JWT token in all requests
  router.on('before', (event) => {
    const token = localStorage.getItem('auth_token')

    // Only check if token is actually expired (not "will expire soon")
    // This prevents aggressive logouts on every request
    if (token && !isRefreshing && authService.isTokenExpired()) {
      console.warn('[Auth] Token expired, attempting refresh before request')

      // Prevent the current request
      event.preventDefault()
      isRefreshing = true

      // Try to refresh the token asynchronously
      authService.refreshJwtToken().then((refreshed) => {
        isRefreshing = false

        if (refreshed) {
          console.log('[Auth] Token refreshed successfully, retrying request')
          // Token refreshed successfully, retry the original request
          const newToken = localStorage.getItem('auth_token')
          const visitOptions = event.detail.visit

          // Check if we're in Telegram WebApp context and add initData
          const telegramInitData = (window as any).Telegram?.WebApp?.initData
          const headers: Record<string, string> = {
            ...visitOptions.headers,
            'Authorization': `Bearer ${newToken}`,
          }
          if (telegramInitData) {
            headers['X-Telegram-Init-Data'] = telegramInitData
          }

          router.visit(visitOptions.url.pathname + visitOptions.url.search, {
            method: visitOptions.method,
            data: visitOptions.data,
            headers: headers,
            replace: visitOptions.replace,
            preserveScroll: visitOptions.preserveScroll,
            preserveState: visitOptions.preserveState,
            only: visitOptions.only,
            except: visitOptions.except,
          })
        } else {
          console.error('[Auth] Token refresh failed, redirecting to login')
          // Refresh failed, redirect to landing page with login modal
          localStorage.removeItem('auth_token')
          localStorage.removeItem('refresh_token')

          const currentPath = window.location.pathname
          const returnTo = currentPath !== '/' ? currentPath : null

          router.visit('/' + (returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''), {
            replace: true,
          })
        }
      }).catch((error) => {
        console.error('[Auth] Token refresh error:', error)
        isRefreshing = false
        // On error, let the request continue - backend will return 401 if needed
      })

      return
    }

    // Build headers object with JWT and Telegram initData if available
    const headers: Record<string, string> = {
      ...event.detail.visit.headers,
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Check if we're in Telegram WebApp context and add initData
    const telegramInitData = (window as any).Telegram?.WebApp?.initData
    if (telegramInitData) {
      headers['X-Telegram-Init-Data'] = telegramInitData
    }

    // Set headers for this request
    event.detail.visit.headers = headers
  })

  // Handle authentication and authorization errors
  // Use 'invalid' event for non-Inertia responses (401, 403, etc.)
  router.on('invalid', (event) => {
    const response = event.detail.response

    // Suppress error dialog for successful JSON responses (e.g., /notifications API
    // accidentally hit via Inertia). These are not errors - just wrong response format.
    if (response && response.status >= 200 && response.status < 300) {
      event.preventDefault()
      return
    }

    // 401 Unauthorized - JWT expired/invalid
    if (response && response.status === 401) {
      console.warn('[Auth] Received 401 Unauthorized response')

      // Try to refresh token before logging out
      if (!isRefreshing) {
        isRefreshing = true
        console.log('[Auth] Attempting token refresh after 401')

        authService.refreshJwtToken().then((refreshed) => {
          isRefreshing = false

          if (refreshed) {
            console.log('[Auth] Token refreshed successfully after 401, reloading page')
            // Token refreshed successfully, reload the current page
            window.location.reload()
          } else {
            console.error('[Auth] Token refresh failed after 401, redirecting to login')
            // Refresh failed, clear tokens and redirect to login
            localStorage.removeItem('auth_token')
            localStorage.removeItem('refresh_token')

            const currentPath = window.location.pathname
            const returnTo = currentPath !== '/' ? currentPath : null

            router.visit('/' + (returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''), {
              replace: true,
            })
          }
        }).catch((error) => {
          console.error('[Auth] Token refresh error after 401:', error)
          isRefreshing = false

          // On error, clear tokens and redirect to login
          localStorage.removeItem('auth_token')
          localStorage.removeItem('refresh_token')

          const currentPath = window.location.pathname
          const returnTo = currentPath !== '/' ? currentPath : null

          router.visit('/' + (returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''), {
            replace: true,
          })
        })
      }

      // Prevent default error handling
      event.preventDefault()
    }

    // 403 Forbidden - Insufficient permissions
    if (response && response.status === 403) {
      // Redirect to dashboard with error message
      router.visit('/', {
        replace: true,
        onFinish: () => {
          // Error message will be shown by error handler in the component
          console.error('Access forbidden: You do not have permission to access this resource')
        }
      })

      // Prevent default error handling
      event.preventDefault()
    }
  })

  // Handle cross-tab logout - redirect to landing page when logged out in another tab
  window.addEventListener('auth:logout', (event: Event) => {
    const customEvent = event as CustomEvent
    if (customEvent.detail?.crossTab) {
      console.log('Detected logout in another tab, redirecting to landing')
      router.visit('/', { replace: true })
    }
  })

  // Handle cross-tab login - optionally reload current page to get fresh data
  window.addEventListener('auth:login', (event: Event) => {
    const customEvent = event as CustomEvent
    if (customEvent.detail?.crossTab) {
      console.log('Detected login in another tab')
      // Optionally reload the current page to get fresh data
      // router.reload()
    }
  })

  // Add global loading indicator
  let loadingTimeout: NodeJS.Timeout | null = null

  router.on('start', () => {
    // Show loading indicator after 250ms (prevents flash for fast requests)
    loadingTimeout = setTimeout(() => {
      document.body.classList.add('inertia-loading')
    }, 250)
  })

  router.on('finish', () => {
    // Clear timeout and hide loading indicator
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      loadingTimeout = null
    }
    document.body.classList.remove('inertia-loading')
  })

  // Broadcast URL to parent window (Cayu) for iframe tracking
  // This allows the refresh button to reload the current page, not just root
  const broadcastUrl = () => {
    // Dispatch event for cayu-sdk to handle (sends to parent Cayu platform)
    window.dispatchEvent(new CustomEvent('cayu:navigate', { detail: { url: window.location.href } }))
  }

  // Broadcast on initial load
  broadcastUrl()

  // Update page store BEFORE React renders the new component.
  // The 'success' event fires before Inertia swaps the page component,
  // so AppLayout has the correct auth state when the new page renders.
  // This prevents the "zoomed/no sidebar" flash on login and the
  // sidebar remnants on logout.
  router.on('success', (event) => {
    pageStore.setProps(event.detail.page.props)
  })

  // Broadcast URL after each navigation (for iframe tracking)
  router.on('navigate', () => {
    broadcastUrl()
  })

  // Global fetch override - adds JWT to all fetch() calls automatically
  const originalFetch = window.fetch
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const headers = new Headers(init?.headers)
    const token = localStorage.getItem('auth_token')

    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    return originalFetch(input, { ...init, headers })
  }

  createInertiaApp({
    // Resolve page components with eager loading
    // NOTE: We don't wrap components with InertiaThemeSync here because it uses usePage()
    // which causes re-renders on every Inertia request, breaking input focus.
    // Theme/preferences are initialized once in setup() instead.
    resolve: async (name) => {
      // Use lazy loading (no eager:true) so new page files are always discovered
      // without requiring a server restart. Eager glob compiles a static list at
      // build time and misses files added after the last inertia.tsx recompile.
      // CRITICAL: Must exclude test files at glob level, not after import,
      // otherwise Vitest mocks get bundled causing runtime errors
      const pages = import.meta.glob<{ default: any }>([
        '../pages/**/*.tsx',
        '!../pages/**/*.test.tsx',
        '!../pages/**/*.spec.tsx',
      ])

      // Direct static imports for pages that may not appear in the glob due to
      // file creation timing relative to Vite's last scan. Static import paths
      // are always analyzed by Vite at compile time regardless of glob state.
      const staticOverrides: Record<string, () => Promise<{ default: any }>> = {
        'Drivers/Candidates/Apply': () => import('../pages/Drivers/Candidates/Apply'),
        'Admin/VacancySettings/Edit': () => import('../pages/Admin/VacancySettings/Edit'),
        'News/Index': () => import('../pages/News/Index'),
        'News/Show': () => import('../pages/News/Show'),
        'News/New': () => import('../pages/News/New'),
        'News/Edit': () => import('../pages/News/Edit'),
      }

      const loader = staticOverrides[name] ?? pages[`../pages/${name}.tsx`]

      if (!loader) {
        throw new Error(`Page not found: ${name}`)
      }

      const page = await loader()

      // Set default layout for all pages (Inertia best practice)
      // This allows using usePage() hook for flash messages
      // Use !== undefined so pages that explicitly set layout = null are respected
      if (page.default.layout === undefined) {
        page.default.layout = name.startsWith('Detailer/')
          ? null
          : ((page: React.ReactNode) => <AppLayout>{page}</AppLayout>)
      }

      return page
    },

    // Setup the app with ErrorBoundary to catch React errors
    // This prevents blank white pages when components crash
    setup({ el, App, props }) {
      // Populate page store with initial props BEFORE rendering.
      // AppLayout (class component) reads from this store instead of usePage()
      // to work around Safari 18.3 hooks dispatcher bug.
      pageStore.setProps(props.initialPage.props)

      const root = createRoot(el)
      root.render(
        <ErrorBoundary>
          <App {...props} />
        </ErrorBoundary>
      )
      // Mark React as mounted - ErrorDialog now handles errors instead of showGlobalError
      reactMounted = true
    },

    // Progress bar at top of page
    progress: {
      color: '#3b82f6',
      showSpinner: false,
    },
  })
}
